import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import {
  Loader2,
  Wallet,
  Plus,
  Trash2,
  Info,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending: { label: 'En attente', className: 'bg-amber-100 text-amber-700 border-0' },
  partial: { label: 'Partiellement déduite', className: 'bg-blue-100 text-blue-700 border-0' },
  applied: { label: 'Soldée', className: 'bg-emerald-100 text-emerald-700 border-0' },
  cancelled: { label: 'Annulée', className: 'bg-slate-100 text-slate-500 border-0' },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const AdvancesDialog = ({ open, onOpenChange, housekeeper }) => {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formAmount, setFormAmount] = useState('');
  const [formReason, setFormReason] = useState('');

  const fetchAdvances = useCallback(async () => {
    if (!housekeeper?.id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/advances?housekeeper_id=${housekeeper.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setAdvances(data);
      } else {
        toast.error('Erreur lors du chargement des avances');
      }
    } catch (e) {
      console.error(e);
      toast.error('Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [housekeeper]);

  useEffect(() => {
    if (open && housekeeper?.id) {
      fetchAdvances();
      setFormAmount('');
      setFormReason('');
    }
  }, [open, housekeeper, fetchAdvances]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const amount = parseFloat(formAmount);
    if (!amount || amount <= 0) {
      toast.error('Montant invalide');
      return;
    }
    if (!formReason.trim() || formReason.trim().length < 2) {
      toast.error('Le motif est obligatoire');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/advances`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          housekeeper_id: housekeeper.id,
          amount,
          reason: formReason.trim(),
        }),
      });
      if (res.ok) {
        toast.success(`Avance de ${amount.toFixed(2)}€ enregistrée`);
        setFormAmount('');
        setFormReason('');
        fetchAdvances();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de la création');
      }
    } catch (e) {
      console.error(e);
      toast.error('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (advanceId) => {
    if (!window.confirm('Annuler cette avance ? (Impossible si déjà déduite)')) return;
    setDeletingId(advanceId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/advances/${advanceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Avance annulée');
        fetchAdvances();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Impossible d'annuler");
      }
    } catch (e) {
      console.error(e);
      toast.error('Erreur réseau');
    } finally {
      setDeletingId(null);
    }
  };

  const totalOutstanding = advances
    .filter((a) => a.status === 'pending' || a.status === 'partial')
    .reduce((sum, a) => sum + (a.remaining_amount || 0), 0);

  const activeAdvances = advances.filter((a) => a.status !== 'cancelled');
  const cancelledAdvances = advances.filter((a) => a.status === 'cancelled');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="advances-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-[#1F3A8A]" />
            Avances sur paiement — {housekeeper?.name}
          </DialogTitle>
        </DialogHeader>

        {/* Summary banner */}
        <div className="bg-gradient-to-r from-[#1F3A8A] to-[#2D4A9A] text-white rounded-lg p-4">
          <p className="text-blue-100 text-sm">Solde d'avances à déduire</p>
          <p className="text-3xl font-bold" data-testid="advances-total-outstanding">
            {totalOutstanding.toFixed(2)} €
          </p>
          <p className="text-blue-200 text-xs mt-1">
            Automatiquement déduit lors de la prochaine facture mensuelle.
            Report du solde d'un mois à l'autre.
          </p>
        </div>

        {/* Create form */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1">
                  <Label htmlFor="advance-amount">Montant (€) *</Label>
                  <Input
                    id="advance-amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="50.00"
                    value={formAmount}
                    onChange={(e) => setFormAmount(e.target.value)}
                    required
                    data-testid="advance-amount-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="advance-reason">Motif *</Label>
                  <Input
                    id="advance-reason"
                    type="text"
                    placeholder="Ex : dépannage vacances, avance sur juillet…"
                    value={formReason}
                    onChange={(e) => setFormReason(e.target.value)}
                    minLength={2}
                    required
                    data-testid="advance-reason-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#1F3A8A] hover:bg-[#162d6d]"
                data-testid="advance-submit-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Enregistrer l'avance
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-[#1F3A8A]" />
          </div>
        ) : advances.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm flex items-center justify-center gap-2">
            <Info className="h-4 w-4" />
            Aucune avance enregistrée pour cette FDM.
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              Historique
            </p>
            {activeAdvances.map((adv) => {
              const st = STATUS_LABELS[adv.status] || STATUS_LABELS.pending;
              const canCancel =
                adv.status === 'pending' &&
                (!adv.applied_amount || adv.applied_amount < 0.005);
              return (
                <div
                  key={adv.id}
                  className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition"
                  data-testid={`advance-row-${adv.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-slate-800">
                          {adv.amount?.toFixed(2)} €
                        </span>
                        <Badge className={st.className}>{st.label}</Badge>
                        <span className="text-xs text-slate-400">
                          {formatDate(adv.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 truncate">{adv.reason}</p>
                      {(adv.applied_amount || 0) > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          Déjà déduit : <strong>{adv.applied_amount.toFixed(2)} €</strong>
                          {' · '}
                          Restant : <strong>{adv.remaining_amount.toFixed(2)} €</strong>
                        </p>
                      )}
                      {adv.applications && adv.applications.length > 0 && (
                        <div className="mt-2 pl-3 border-l-2 border-blue-200 space-y-0.5">
                          {adv.applications.map((app, i) => (
                            <p key={i} className="text-xs text-slate-500">
                              <CheckCircle2 className="h-3 w-3 inline mr-1 text-blue-500" />
                              Facture {app.invoice_number} ({String(app.month).padStart(2, '0')}/{app.year}) : -{app.amount?.toFixed(2)} €
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                    {canCancel && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCancel(adv.id)}
                        disabled={deletingId === adv.id}
                        title="Annuler l'avance"
                        data-testid={`advance-cancel-${adv.id}`}
                      >
                        {deletingId === adv.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-red-500" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {cancelledAdvances.length > 0 && (
              <details className="mt-3">
                <summary className="text-xs text-slate-400 cursor-pointer">
                  Avances annulées ({cancelledAdvances.length})
                </summary>
                <div className="mt-2 space-y-1">
                  {cancelledAdvances.map((adv) => (
                    <div
                      key={adv.id}
                      className="text-xs text-slate-400 line-through px-3 py-1"
                    >
                      {adv.amount?.toFixed(2)} € — {adv.reason} ({formatDate(adv.created_at)})
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdvancesDialog;
